import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PurchaseModal from '../components/manualPurchase/PurchaseModal';
import EvidenceViewer from '../components/manualPurchase/EvidenceViewer';
import PurchaseSummary, {
  PurchaseError,
} from '../components/manualPurchase/PurchaseSummary';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';
import service from '../services/manualPurchaseService';
import {
  clearPurchaseIntent,
  formatPurchaseTime,
  isPurchaseFulfilled,
  readPurchaseIntent,
} from '../utils/manualPurchase';

const buttonClass =
  'rounded-md border border-gray-400 bg-white py-[9px] text-base hover:bg-gray-50 disabled:opacity-50';

const PurchaseDetail = ({ id, userId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [purchase, setPurchase] = useState(() =>
    location.state?.purchase?.id === id ? location.state.purchase : null
  );
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const retryLock = useRef(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [evidenceName, setEvidenceName] = useState('');
  const timeZone = location.state?.timeZone;
  const evidenceId = purchase?.purchase.evidenceId;
  const fulfilled = isPurchaseFulfilled(purchase);

  useEffect(() => {
    if (!fulfilled) return;
    try {
      // Completion ends recovery even when the user leaves through the URL bar.
      const intent = readPurchaseIntent(userId);
      if (intent?.id === id) clearPurchaseIntent(userId);
    } catch (err) {
      setError(err);
    }
  }, [fulfilled, id, userId]);

  useEffect(() => {
    let active = true;
    setEvidenceName('');
    if (evidenceId)
      service
        .getEvidence(evidenceId)
        .then(result => {
          if (active) setEvidenceName(result.fileName);
        })
        .catch(() => {
          /* Opening the image shows access errors with a retry action. */
        });
    return () => {
      active = false;
    };
  }, [evidenceId]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    let attempt = 0;
    if (retrying) return undefined;
    const poll = async () => {
      let again = true;
      try {
        const result = await service.getPurchase(id);
        if (!active) return;
        setPurchase(result);
        setError(null);
        again = !isPurchaseFulfilled(result) && result.status === 'processing';
      } catch (err) {
        if (!active) return;
        setError(err);
        again = ![400, 401, 403, 404].includes(err.status);
      } finally {
        if (active) setLoading(false);
      }
      if (active && again) {
        timer = setTimeout(
          poll,
          Math.min(30000, 3000 * Math.pow(1.5, attempt++))
        );
      }
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [id, retrying]);

  const retry = async () => {
    if (!purchase?.retryable || retryLock.current) return;
    retryLock.current = true;
    setRetrying(true);
    setError(null);
    setActionError(null);
    try {
      const result = await service.retry(id);
      if (mounted.current) setPurchase(result);
    } catch (err) {
      if (mounted.current) setActionError(err);
    } finally {
      retryLock.current = false;
      if (mounted.current) setRetrying(false);
    }
  };

  const leavePurchase = destination => {
    try {
      if (isPurchaseFulfilled(purchase)) {
        const intent = readPurchaseIntent(userId);
        if (intent?.id === id) clearPurchaseIntent(userId);
      }
      // A dismissed result should not reopen through browser Back/Forward.
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err);
    }
  };

  const newPurchase = () => leavePurchase(ROUTES.MANUAL_PURCHASE);
  const completedNow = location.state?.created && isPurchaseFulfilled(purchase);
  const close = () => leavePurchase(`${ROUTES.PURCHASES}${location.search}`);
  return (
    <>
      <PurchaseModal
        open
        success={!!completedNow}
        title={completedNow ? 'Purchase Completed' : 'Purchases Details'}
        subtitle={
          completedNow
            ? 'The event ticket item and applicable distributions were created.'
            : 'View detailed information about purchases.'
        }
        busy={retrying}
        onClose={close}
        footer={
          completedNow ? (
            <>
              <button
                type='button'
                className={`${buttonClass} w-[120px]`}
                onClick={newPurchase}
              >
                Create another
              </button>
              <button
                type='button'
                className='w-[70px] rounded-md bg-blue-600 py-[10px] text-base text-white'
                onClick={close}
              >
                Done
              </button>
            </>
          ) : (
            <>
              {!isPurchaseFulfilled(purchase) &&
                purchase?.status === 'failed' &&
                purchase.retryable && (
                  <button
                    type='button'
                    className={`${buttonClass} px-3`}
                    disabled={retrying}
                    onClick={retry}
                  >
                    {retrying
                      ? 'Retrying ticket issuance…'
                      : 'Retry ticket issuance'}
                  </button>
                )}
              <button
                type='button'
                className='w-[127px] rounded-md bg-blue-600 py-[10px] text-base text-white'
                disabled={retrying}
                onClick={close}
              >
                Close Details
              </button>
            </>
          )
        }
      >
        <PurchaseError
          error={
            actionError ||
            error ||
            (!isPurchaseFulfilled(purchase) && purchase?.status === 'failed'
              ? {
                  message: purchase.retryable
                    ? 'Ticket issuance failed. Retry ticket issuance to complete this purchase.'
                    : 'This purchase could not be completed. Contact support for assistance.',
                }
              : null)
          }
        />
        {loading && !purchase && (
          <p role='status' className='text-sm text-gray-500'>
            Loading purchase…
          </p>
        )}
        {purchase && (
          <>
            {completedNow ? (
              <dl className='space-y-[15px] text-base'>
                {[
                  ['Order', purchase.orderId],
                  ['Creator', purchase.purchase.creator.name],
                  ['Reference', purchase.purchase.reference],
                  [
                    'Payment date and time',
                    formatPurchaseTime(purchase.purchase.paymentAt, timeZone),
                  ],
                  ['Item', purchase.purchase.item.name],
                ].map(([label, value]) => (
                  <div className='flex justify-between gap-5' key={label}>
                    <dt className='shrink-0 text-gray-500'>{label}</dt>
                    <dd className='min-w-0 break-words text-right text-[#344154]'>
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <PurchaseSummary
                purchase={purchase.purchase}
                timeZone={timeZone}
                evidenceName={evidenceName}
                onViewEvidence={
                  evidenceId ? () => setShowEvidence(true) : undefined
                }
              />
            )}
          </>
        )}
      </PurchaseModal>
      <EvidenceViewer
        evidenceId={evidenceId}
        open={showEvidence}
        onClose={() => setShowEvidence(false)}
      />
    </>
  );
};

const ManualPurchaseDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  return <PurchaseDetail key={`${user.id}:${id}`} id={id} userId={user.id} />;
};

export default ManualPurchaseDetailPage;
